import React, { FC } from "react";
import { userAccountService } from "../services";
import {
  IAgentCodeUrlParam,
  ICreateYPApplicationRequestBody,
} from "../services/user-account-service.types";
import {
  IBillingAddress,
  IOnlineApplication,
} from "../services/online-application.types";
import { ICompileTimeError } from "../config/common";
import { useApplication } from "./ApplicationContext";
import { INITIAL_FORM_VALUES } from "../formModels/initialFormValues";

interface IYPApplicationContextParams {
  createYPApplication: (
    params: IAgentCodeUrlParam,
    body: ICreateYPApplicationRequestBody,
    address?: IBillingAddress
  ) => Promise<boolean | ICompileTimeError>;
}

export const YPApplicationContext =
  React.createContext<IYPApplicationContextParams>({
    createYPApplication: () => new Promise(() => {}),
  });

export const YPApplicationContextProvider: FC<
  React.PropsWithChildren<React.PropsWithChildren>
> = ({ children }) => {
  const { setApplicationData, setApplicationDetails } = useApplication();

  const createYPApplication = async (
    params: IAgentCodeUrlParam,
    body: ICreateYPApplicationRequestBody
  ): Promise<boolean | ICompileTimeError> => {
    try {
      const applicationDataResponse =
        await userAccountService.createYPApplication(params, body);

      setApplicationDetails({
        ...applicationDataResponse,
        ypDateOfBirth: applicationDataResponse.dateOfBirth,
        ypMobileNumber: applicationDataResponse.mobileNumber,
        address: body.address,
      } as IOnlineApplication);

      // new application start with blank app
      setApplicationData(INITIAL_FORM_VALUES);

      return true;
    } catch (error: unknown) {
      return (error as ICompileTimeError) || false;
    }
  };

  return (
    <YPApplicationContext.Provider
      value={{
        createYPApplication,
      }}
    >
      {children}
    </YPApplicationContext.Provider>
  );
};

export const useYPApplication = () => React.useContext(YPApplicationContext);
